import { useState, useEffect, useRef } from 'react';
import { preloadSceneSounds, playSceneSound, stopSceneSounds } from '@/components/radar/radarSound';

const SCENE_SOUNDS = {
  summon: '/sounds/shenron-summon.mp3?v=2',
  selected: '/sounds/shenron-wish-selected.mp3',
  wishGranted: '/sounds/wish-granted.mp3?v=2',
  shenronGranted: '/sounds/shenron-wish-granted.mp3',
  disperse: '/sounds/dragonballs-disperse.mp3?v=2',
};
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
import { motion, AnimatePresence } from 'framer-motion';
import { dragonBallAPI } from '@/utils/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { emitEvent, DRAGON_BALL_WISH_MADE } from '@/utils/events';

const SHENRON_GIF = "https://customer-assets.emergentagent.com/job_dragon-sphere-tv/artifacts/nyo5q1b1_dbz-dragon-ball-z.gif";

// Aura colors for the reveal animation
const AURA_COLORS = {
  ssj: { primary: '#FFD700', secondary: '#FFA500', name: 'Super Saiyan' },
  ssj2: { primary: '#FFD700', secondary: '#FFFFFF', name: 'Super Saiyan 2' },
  ssj3: { primary: '#FFD700', secondary: '#FFFF00', name: 'Super Saiyan 3' },
  ssjgod: { primary: '#FF4500', secondary: '#FF0000', name: 'Super Saiyan God' },
  ssjblue: { primary: '#00BFFF', secondary: '#1E90FF', name: 'Super Saiyan Blue' },
  ui: { primary: '#C0C0C0', secondary: '#FFFFFF', name: 'Ultra Instinct' },
  ue: { primary: '#8B00FF', secondary: '#4B0082', name: 'Ultra Ego' },
  berserk: { primary: '#32CD32', secondary: '#00FF00', name: 'Legendary (Berserk)' },
  rose: { primary: '#FF69B4', secondary: '#FF1493', name: 'Super Saiyan Rosé' },
  orange: { primary: '#FF8C00', secondary: '#FF4500', name: 'Orange Piccolo' },
  beast: { primary: '#FF0000', secondary: '#8B0000', name: 'Beast (Gohan)' },
  black: { primary: '#000000', secondary: '#4B0082', name: 'Goku Black' }
};

// Shenron dialogue lines
const SHENRON_DIALOGUES = {
  awakening: [
    "...",
    "WHO HAS SUMMONED ME?",
    "I AM THE ETERNAL DRAGON, SHENRON.",
  ],
  ready: "SPEAK YOUR WISH. I SHALL GRANT ANY WISH WITHIN MY POWER.",
  thinking: "HMMMM...",
  granted: "YOUR WISH HAS BEEN GRANTED.",
  farewell: "FAREWELL... UNTIL WE MEET AGAIN.",
};

const WISH_OPTIONS = [
  {
    id: "power",
    name: "Ultimate Power",
    description: "Receive a random aura effect (100 Zeni + 3 balls back if duplicate)",
    icon: "⚡",
    shenronResponse: "YOU SEEK POWER... I SHALL BESTOW UPON YOU A WARRIOR'S AURA."
  },
  {
    id: "immortality",
    name: "Eternal Glory", 
    description: "Obtain the legendary Shenron avatar frame",
    icon: "👑",
    shenronResponse: "YOU DESIRE GLORY... I SHALL BESTOW UPON YOU MY BLESSING."
  },
  {
    id: "wealth",
    name: "Wealth",
    description: "Receive 1,000 Zeni to spend in the shop",
    icon: "💰",
    shenronResponse: "WEALTH YOU SHALL HAVE... I BESTOW UPON YOU 1,000 ZENI!"
  },
  {
    id: "revival",
    name: "Second Chance",
    description: "3 days of ad-free viewing (adds 3 more if already active)",
    icon: "🔄",
    shenronResponse: "A SECOND CHANCE... THE EVIL CONTAINMENT WAVE SHALL SEAL ALL ADS."
  }
];

// Aura Reveal Animation Component
const AuraRevealAnimation = ({ auraData, onComplete }) => {
  const [phase, setPhase] = useState('charging'); // charging, reveal, complete
  const auraInfo = AURA_COLORS[auraData.aura] || { primary: '#FFD700', secondary: '#FFFFFF', name: auraData.aura_name };
  const isDuplicate = auraData.is_duplicate;

  // Parent re-renders every second (timers), so keep onComplete in a ref and start the timers only once
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    // Charging phase: 2 seconds
    const timer1 = setTimeout(() => setPhase('reveal'), 2000);
    // Reveal phase: 3 seconds then complete
    const timer2 = setTimeout(() => setPhase('complete'), 5000);
    const timer3 = setTimeout(() => onCompleteRef.current(), 6000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center text-center px-4"
    >
      {/* Aura Energy Ball */}
      <div className="relative mb-8">
        {/* Outer glow */}
        <motion.div
          className="absolute inset-0 rounded-full blur-3xl"
          style={{ backgroundColor: auraInfo.primary }}
          animate={{
            scale: phase === 'charging' ? [1, 1.5, 1] : phase === 'reveal' ? [1.5, 3, 2] : 2,
            opacity: phase === 'charging' ? [0.3, 0.6, 0.3] : phase === 'reveal' ? [0.6, 1, 0.8] : 0.8,
          }}
          transition={{ 
            duration: phase === 'charging' ? 1 : 0.5, 
            repeat: phase === 'charging' ? Infinity : 0 
          }}
        />
        
        {/* Inner energy sphere */}
        <motion.div
          className="relative w-32 h-32 sm:w-48 sm:h-48 rounded-full flex items-center justify-center"
          style={{ 
            background: `radial-gradient(circle, ${auraInfo.secondary} 0%, ${auraInfo.primary} 50%, transparent 70%)`,
            boxShadow: `0 0 60px ${auraInfo.primary}, 0 0 100px ${auraInfo.secondary}`
          }}
          animate={{
            scale: phase === 'charging' ? [0.8, 1.2, 0.8] : phase === 'reveal' ? [1.2, 1.5, 1.3] : 1.3,
            rotate: phase === 'charging' ? [0, 360] : 0,
          }}
          transition={{ 
            duration: phase === 'charging' ? 2 : 1, 
            repeat: phase === 'charging' ? Infinity : 0,
            ease: "linear"
          }}
        >
          {/* Aura icon */}
          <motion.span 
            className="text-5xl sm:text-7xl"
            animate={{ 
              scale: phase === 'reveal' ? [1, 1.5, 1.2] : 1,
              opacity: phase === 'complete' ? 1 : [0.5, 1, 0.5]
            }}
            transition={{ duration: 0.5 }}
          >
            ⚡
          </motion.span>
        </motion.div>

        {/* Energy particles */}
        {phase === 'charging' && (
          <>
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-3 h-3 rounded-full"
                style={{ backgroundColor: auraInfo.secondary }}
                initial={{ 
                  x: Math.cos(i * 45 * Math.PI / 180) * 100,
                  y: Math.sin(i * 45 * Math.PI / 180) * 100,
                  opacity: 0
                }}
                animate={{ 
                  x: 0, y: 0, opacity: [0, 1, 0],
                  scale: [0.5, 1, 0]
                }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  delay: i * 0.2 
                }}
              />
            ))}
          </>
        )}
      </div>

      {/* Text reveal */}
      <AnimatePresence mode="wait">
        {phase === 'charging' && (
          <motion.div
            key="charging"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <p className="text-xl sm:text-3xl font-bold text-yellow-400 animate-pulse">
              CHANNELING POWER...
            </p>
          </motion.div>
        )}

        {phase === 'reveal' && (
          <motion.div
            key="reveal"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            <motion.p
              className="text-2xl sm:text-4xl font-bold drop-shadow-lg"
              style={{ color: auraInfo.primary, textShadow: `0 0 20px ${auraInfo.primary}` }}
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 0.5, repeat: 2 }}
            >
              {auraInfo.name.toUpperCase()}
            </motion.p>
            <p className="text-lg sm:text-xl text-white">
              {isDuplicate ? "AURA OBTAINED!" : "NEW AURA UNLOCKED!"}
            </p>
          </motion.div>
        )}

        {phase === 'complete' && (
          <motion.div
            key="complete"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <p 
              className="text-2xl sm:text-4xl font-bold"
              style={{ color: auraInfo.primary, textShadow: `0 0 20px ${auraInfo.primary}` }}
            >
              {auraInfo.name.toUpperCase()}
            </p>
            
            {isDuplicate ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-3"
              >
                <div className="flex items-center justify-center gap-2 text-orange-400">
                  <span className="text-2xl">🔄</span>
                  <p className="text-lg sm:text-xl font-bold">DUPLICATE AURA!</p>
                </div>
                <p className="text-gray-300 text-sm sm:text-base">
                  You already possess this power...
                </p>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-green-900/50 border border-green-500/50 rounded-lg p-4 mt-4"
                >
                  <p className="text-green-400 font-bold text-lg mb-2">COMPENSATION RECEIVED:</p>
                  <div className="flex items-center justify-center gap-6">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">💰</span>
                      <span className="text-yellow-400 font-bold">+100 Zeni</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🟠</span>
                      <span className="text-orange-400 font-bold">+3 Dragon Balls</span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-2"
              >
                <p className="text-green-400 text-lg sm:text-xl font-bold">
                  ✨ AURA EQUIPPED! ✨
                </p>
                <p className="text-gray-400 text-sm">
                  Your new aura has been automatically equipped.
                </p>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const ShenronWishModal = ({ isOpen, onClose, onWishComplete }) => {
  const { checkAuth } = useAuth();
  const [stage, setStage] = useState('summon'); // summon, awakening, ready, selecting, granting, aura_reveal, farewell
  const [dialogueIndex, setDialogueIndex] = useState(0);
  const [selectedWish, setSelectedWish] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rewardMessage, setRewardMessage] = useState('');
  const [rewardData, setRewardData] = useState(null);

  const sessionRef = useRef(0);
  const grantedRef = useRef(Promise.resolve());

  useEffect(() => {
    sessionRef.current += 1;
    stopSceneSounds();
    if (!isOpen) {
      // Reset state when modal closes
      setStage('summon');
      setDialogueIndex(0);
      setSelectedWish(null);
      setRewardMessage('');
      setRewardData(null);
    }
  }, [isOpen]);

  const playOnly = (url) => { stopSceneSounds(); playSceneSound(url); };

  const handleSummon = () => {
    preloadSceneSounds(Object.values(SCENE_SOUNDS));
    playOnly(SCENE_SOUNDS.summon);
    setStage('awakening');
    // Play through awakening dialogue
    let idx = 0;
    const interval = setInterval(() => {
      setDialogueIndex(idx);
      idx++;
      if (idx >= SHENRON_DIALOGUES.awakening.length) {
        clearInterval(interval);
        setTimeout(() => setStage('ready'), 1500);
      }
    }, 2000);
  };

  const handleSelectWish = (wish) => {
    playOnly(SCENE_SOUNDS.selected);
    setSelectedWish(wish);
    setStage('selecting');
  };

  // Farewell starts once both "granted" sounds finish; the disperse sound plays as the balls scatter
  const finishWithFarewell = async () => {
    const session = sessionRef.current;
    await grantedRef.current;
    if (session !== sessionRef.current) return;
    setStage('farewell');
    checkAuth();
    emitEvent(DRAGON_BALL_WISH_MADE);
    await Promise.all([playSceneSound(SCENE_SOUNDS.disperse), wait(3000)]);
    if (session !== sessionRef.current) return;
    toast.success('Your wish has been granted!');
    onWishComplete();
    onClose();
  };

  const handleConfirmWish = async () => {
    if (!selectedWish) return;
    
    setIsProcessing(true);
    setStage('granting');
    
    try {
      const response = await dragonBallAPI.makeWish(selectedWish.id);
      setRewardMessage(response.data.reward_message);
      setRewardData(response.data.reward_data);
      const session = sessionRef.current;
      stopSceneSounds();
      grantedRef.current = playSceneSound(SCENE_SOUNDS.wishGranted)
        .then(() => session === sessionRef.current && playSceneSound(SCENE_SOUNDS.shenronGranted));
      
      // Check if this is a power wish with aura data
      if (selectedWish.id === 'power' && response.data.reward_data?.aura) {
        // Show aura reveal animation
        setTimeout(() => {
          setStage('aura_reveal');
        }, 2000);
      } else {
        // Standard wish flow
        await wait(3000);
        finishWithFarewell();
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make wish');
      setStage('ready');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAuraRevealComplete = () => finishWithFarewell();

  const handleCancel = () => {
    setSelectedWish(null);
    setStage('ready');
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] bg-black"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        width: '100vw',
        height: '100vh',
        minHeight: '-webkit-fill-available'
      }}
    >
      {/* Dark sky background with lightning effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-green-950 to-black overflow-hidden">
        {/* Lightning flashes */}
        <motion.div
          className="absolute inset-0 bg-green-500/20"
          animate={{ opacity: [0, 0.3, 0, 0.5, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
        />
      </div>

      {/* Close button - always visible */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-[10001] p-3 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors shadow-lg"
        style={{ position: 'fixed', top: '1rem', right: '1rem' }}
      >
        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Scrollable content container */}
      <div className="absolute inset-0 overflow-y-auto">
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 py-8 sm:py-12 flex flex-col items-center justify-center min-h-full">
          
          {/* Summon Stage */}
          {stage === 'summon' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center px-2"
            >
              <h1 className="text-2xl sm:text-4xl md:text-6xl font-heading text-yellow-400 mb-4 sm:mb-8 drop-shadow-[0_0_20px_rgba(250,204,21,0.5)]">
                ALL 7 DRAGON BALLS COLLECTED!
              </h1>
              <p className="text-base sm:text-xl text-gray-300 mb-6 sm:mb-12">
                The Dragon Balls are glowing... Summon the Eternal Dragon?
              </p>
              <motion.button
                onClick={handleSummon}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-green-600 to-green-500 text-white text-base sm:text-xl font-bold rounded-lg hover:from-green-500 hover:to-green-400 transition-all shadow-[0_0_30px_rgba(34,197,94,0.5)]"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                SUMMON SHENRON
              </motion.button>
              <p className="mt-4 sm:mt-6 text-sm sm:text-lg text-yellow-300 italic px-4">
                "Eternal Dragon, by your name I summon you! COME FORTH, SHENRON!"
              </p>
            </motion.div>
          )}

          {/* Awakening Stage */}
          {stage === 'awakening' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center px-2"
            >
              <motion.img
                src={SHENRON_GIF}
                alt="Shenron"
                className="w-full max-w-sm sm:max-w-xl md:max-w-2xl mx-auto rounded-lg shadow-[0_0_50px_rgba(34,197,94,0.5)]"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1 }}
              />
              <motion.p
                key={dialogueIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 sm:mt-8 text-xl sm:text-3xl md:text-4xl font-bold text-green-400 drop-shadow-[0_0_10px_rgba(34,197,94,0.8)]"
              >
                {SHENRON_DIALOGUES.awakening[dialogueIndex]}
              </motion.p>
            </motion.div>
          )}

          {/* Ready Stage - Show wish options */}
          {stage === 'ready' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center w-full px-2"
            >
              <img
                src={SHENRON_GIF}
                alt="Shenron"
                className="w-full max-w-xs sm:max-w-md md:max-w-xl mx-auto rounded-lg shadow-[0_0_50px_rgba(34,197,94,0.5)] mb-4 sm:mb-6"
              />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-base sm:text-2xl md:text-3xl font-bold text-green-400 mb-4 sm:mb-8 drop-shadow-[0_0_10px_rgba(34,197,94,0.8)] px-2"
              >
                {SHENRON_DIALOGUES.ready}
              </motion.p>
              
              <div className="grid grid-cols-2 gap-2 sm:gap-4 max-w-2xl mx-auto">
                {WISH_OPTIONS.map((wish, index) => (
                  <motion.button
                    key={wish.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleSelectWish(wish)}
                    className="p-2 sm:p-4 bg-black/50 border-2 border-green-500/50 rounded-lg hover:border-green-400 hover:bg-green-900/30 transition-all text-left"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                      <span className="text-xl sm:text-3xl">{wish.icon}</span>
                      <span className="text-sm sm:text-xl font-bold text-white">{wish.name}</span>
                    </div>
                    <p className="text-[10px] sm:text-sm text-gray-400">{wish.description}</p>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Selecting Stage - Confirm wish */}
          {stage === 'selecting' && selectedWish && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center w-full px-2"
            >
              <img
                src={SHENRON_GIF}
                alt="Shenron"
                className="w-full max-w-xs sm:max-w-md md:max-w-xl mx-auto rounded-lg shadow-[0_0_50px_rgba(34,197,94,0.5)] mb-4 sm:mb-6"
              />
              <motion.p
                className="text-base sm:text-2xl md:text-3xl font-bold text-green-400 mb-2 sm:mb-4 px-2"
              >
                {selectedWish.shenronResponse}
              </motion.p>
              <p className="text-sm sm:text-xl text-yellow-300 mb-4 sm:mb-8">
                IS THIS YOUR WISH?
              </p>
              
              <div className="p-3 sm:p-6 bg-black/50 border-2 border-yellow-500/50 rounded-lg max-w-md mx-auto mb-4 sm:mb-8">
                <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
                  <span className="text-2xl sm:text-4xl">{selectedWish.icon}</span>
                  <span className="text-lg sm:text-2xl font-bold text-white">{selectedWish.name}</span>
                </div>
                <p className="text-xs sm:text-base text-gray-400">{selectedWish.description}</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                <motion.button
                  onClick={handleCancel}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base bg-gray-700 text-white font-bold rounded-lg hover:bg-gray-600 transition-all"
                  whileHover={{ scale: 1.05 }}
                >
                  CHOOSE ANOTHER
                </motion.button>
                <motion.button
                  onClick={handleConfirmWish}
                  disabled={isProcessing}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold rounded-lg hover:from-yellow-500 hover:to-yellow-400 transition-all shadow-[0_0_20px_rgba(250,204,21,0.5)] disabled:opacity-50"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  GRANT MY WISH!
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Granting Stage */}
          {stage === 'granting' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center px-2"
            >
              <img
                src={SHENRON_GIF}
                alt="Shenron"
                className="w-full max-w-xs sm:max-w-md md:max-w-xl mx-auto rounded-lg shadow-[0_0_80px_rgba(250,204,21,0.8)] mb-4 sm:mb-6"
              />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xl sm:text-3xl md:text-4xl font-bold text-yellow-400 drop-shadow-[0_0_20px_rgba(250,204,21,0.8)]"
              >
                {SHENRON_DIALOGUES.granted}
              </motion.p>
              {!rewardData?.aura && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  className="mt-2 sm:mt-4 text-base sm:text-xl text-green-300 px-4"
                >
                  {rewardMessage}
                </motion.p>
              )}
            </motion.div>
          )}

          {/* Aura Reveal Stage - Special animation for power wish */}
          {stage === 'aura_reveal' && rewardData && (
            <AuraRevealAnimation 
              auraData={rewardData} 
              onComplete={handleAuraRevealComplete} 
            />
          )}

          {/* Farewell Stage */}
          {stage === 'farewell' && (
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              transition={{ duration: 3 }}
              className="text-center px-2"
            >
              <motion.img
                src={SHENRON_GIF}
                alt="Shenron"
                className="w-full max-w-xs sm:max-w-md md:max-w-xl mx-auto rounded-lg"
                animate={{ 
                  opacity: [1, 0.8, 0.5, 0],
                  scale: [1, 1.1, 1.2, 1.5],
                }}
                transition={{ duration: 3 }}
              />
              <motion.p
                className="text-xl sm:text-3xl md:text-4xl font-bold text-green-400 mt-4 sm:mt-8"
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 2, delay: 1 }}
              >
                {SHENRON_DIALOGUES.farewell}
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: 3, delay: 2 }}
                className="mt-2 sm:mt-4 text-sm sm:text-lg text-yellow-300"
              >
                The Dragon Balls scatter across the world...
              </motion.p>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ShenronWishModal;
